import { useContext, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import styles from "../../../assets/styles/componentStyles/billingStyles/buyAiCreditsPopup.module.scss";

import GlobalPopUp from "../../utility/globalPopUp";
import GlobalButton from "../../utility/globalButton/button";
import useGenericHook from "../../../hooks/useGenericHook";
import { thousandSeparator } from "../../../utils/constants";
import { useBillingStore } from "../../../store/billingStore";
import { useShallow } from "zustand/react/shallow";
import { UserContext, UserContextPayload } from "../../../context/userContext";
import { useGenericStore } from "../../../store/genericStore";
import { COMPLIMENTARY_AI_CREDITS_FEATURE } from "../constant.billing";
import agent from "../../../services/api";
import { purchaseAiCreditsPayload } from "../../../models/axiosModel";

interface BuyAiCreditsPopupProps {
    open: boolean;
    onClose: () => void;
}

const BuyAiCreditsPopup = (props: BuyAiCreditsPopupProps) => {

    const { open, onClose } = props;

    const { billingAddOns, getBillingAddOnsApi, billingSubscriptionDetails, isBillingAddOnsLoading } = useBillingStore(useShallow((state) => ({
        billingAddOns: state.billingAddOns,
        getBillingAddOnsApi: state.getBillingAddOnsApi,
        billingSubscriptionDetails: state.billingSubscriptionDetails,
        isBillingAddOnsLoading: state.isBillingAddOnsLoading,
    })));

    const { showError } = useGenericStore(useShallow((state) => ({
        showError: state.showError,
    })));
    
    const { isScreenBelow600 } = useGenericHook();
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;

    const [quantity, setQuantity] = useState(1);

    // Functions
    const currentPlanCurrency = billingSubscriptionDetails?.current_plan?.billing_plan_currency?.toLowerCase();

    const aiCreditsAddOn = useMemo(() => {
        if (!billingAddOns?.length || !currentPlanCurrency) return null;

        return billingAddOns.find((addOn) =>
            addOn.billing_feature_name?.toLowerCase() === COMPLIMENTARY_AI_CREDITS_FEATURE
            && addOn.billing_add_on_currency?.toLowerCase() === currentPlanCurrency
        ) ?? null;
    }, [billingAddOns, currentPlanCurrency]);

    const creditsPerPack = Number(aiCreditsAddOn?.billing_plan_feature_limit) || 0;
    const packAmount = Number(aiCreditsAddOn?.billing_add_on_amount) || 0;
    const currencySymbol = aiCreditsAddOn?.billing_add_on_currency_symbol || "";
    const creditsAdded = creditsPerPack * quantity;
    const totalAmount = packAmount * quantity;

    const onClosePopup = () => {
        setQuantity(1);
        onClose();
    };

    // Handle Purchase AI Credits
    const handlePurchaseAiCredits = async () => {
        
        if(!aiCreditsAddOn || quantity <= 0) {
            showError("Please select a valid pack and enter a valid quantity");
            return;
        }

        const payload: purchaseAiCreditsPayload = {
            billing_add_on_id: aiCreditsAddOn?.billing_add_on_id,
            quantity: quantity,
        }
        
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));

        const response = await agent.purchaseAiCredits.post(payload);

        if(response?.status && response?.data) {

            const responseData = Array.isArray(response.data) ? response.data[0] : response.data;
            const invoiceURL = responseData?.invoice_url;

            if (invoiceURL) {
                window.open(invoiceURL, "_blank");
            }

            onClosePopup();
        } else {
            handleError(response);
        }

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    };

    // Effects
    const fetchAddOns = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        
        const res = await getBillingAddOnsApi();
        
        if (!res?.status) {
            showError(res?.error?.message || "Something went wrong");
        }
        
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    };

    useEffect(() => {
        if (open && !billingAddOns) {
            fetchAddOns();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if(isBillingAddOnsLoading) {
        return <></>;
    }

    return (
        <GlobalPopUp
            open={open}
            onClose={onClosePopup}
            handleClose={onClosePopup}
            flagShowCloseIcon
            flagBackdrop
            titlePadding="5px"
            borderRadius="10px"
            paperMinWidth={isScreenBelow600 ? "90%" : "500px"}
            paperMaxwidth={isScreenBelow600 ? "90%" : "550px"}
        >
            <Box className={styles.content}>

                {/* Header */}
                <Box className={styles.header}>
                    <Typography className={styles.title}>
                        Buy AI Credits
                    </Typography>

                    <Typography className={styles.description}>
                        Each pack adds {thousandSeparator(creditsPerPack)} credits. Unused credits carry forward and never expire.
                    </Typography>
                </Box>

                {/* Pack Card */}
                <Box className={styles.packCard}>

                    <Box className={styles.packHeader}>
                        <Box className={styles.packInfo}>
                            <Typography className={styles.packName}>
                                AI Credits Pack
                            </Typography>

                            <Typography className={styles.packMeta}>
                                {thousandSeparator(creditsPerPack)} credits per pack
                            </Typography>
                        </Box>

                        <Typography className={styles.packPrice}>
                            {currencySymbol}{thousandSeparator(packAmount)}

                            <span className={styles.packPriceSuffix}>
                                 /pack
                            </span>
                        </Typography>
                    </Box>

                    <Box className={styles.packsRow}>
                        <Typography className={styles.packsLabel}>
                            Packs
                        </Typography>

                        <Box className={styles.packsControls}>
                            <Box className={styles.quantityControl}>
                                <IconButton
                                    className={styles.quantityBtn}
                                    onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                                    disabled={quantity <= 1}
                                    size="small"
                                >
                                    <RemoveIcon fontSize="small" />
                                </IconButton>

                                <Typography className={styles.quantityValue}>
                                    {quantity}
                                </Typography>

                                <IconButton
                                    className={styles.quantityBtn}
                                    onClick={() => setQuantity((prev) => prev + 1)}
                                    size="small"
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            <Typography className={styles.creditsAdded}>
                                {thousandSeparator(creditsAdded)} credits added
                            </Typography>
                        </Box>
                    </Box>

                </Box>

                {/* Total Card */}
                <Box className={styles.totalCard}>
                    <Typography className={styles.totalLabel}>
                        Total
                    </Typography>

                    <Typography className={styles.totalAmount}>
                        {currencySymbol}{thousandSeparator(totalAmount)}
                    </Typography>
                </Box>

                {/* Actions */}
                <Box className={styles.actions}>
                    <GlobalButton
                        buttonText="Cancel"
                        className="borderedButtonStyle"
                        onClick={onClosePopup}
                    />

                    <GlobalButton
                        buttonText="Confirm purchase"
                        className="primaryButtonStyle"
                        onClick={handlePurchaseAiCredits}
                    />
                </Box>

            </Box>
        </GlobalPopUp>
    );
};

export default BuyAiCreditsPopup;
